import { z } from 'zod';
import {
  SitrepCommunityImpactSchema,
  SitrepResourceRequestsSchema,
  SitrepStagingAreasSchema,
  SitrepSectorsSchema,
} from './sitrep';
import { IncidentReactivationSchema } from './incident';

/**
 * After-Action Report (#612) — the closing beat of the incident workflow (Ontario IMS 2.0 §8
 * Demobilization → After-Action Report, App A #1). The report has two halves:
 *
 *  1. THE FACTUAL SPINE — a pure read-model over what the platform already recorded. Its
 *     operational rollups are PROJECTED FROM THE FINAL SITREP's frozen `structured_data`
 *     (report_version='final'), never re-computed — a second computation path that could
 *     disagree with the Final SITREP is a manufactured contradiction in a legal record.
 *  2. THE AUTHORED HALF — human-written findings / recommendations / debrief notes, each
 *     carrying an author (App A #1 "make recommendations for improvement"; App A #17 debriefing
 *     results are "included in the after-action report"). The COP authors nothing
 *     (records-not-confers); no entry may name an individual (the person-evaluation ban — App A
 *     #1 evaluates the ACTIONS of responders, never the people; an FOI-disclosable record).
 */

/* ------------------------------------------------------------------ *
 * Entry — the AUTHORED half
 * ------------------------------------------------------------------ */

/** Backend identifiers (column values), NOT operator-facing copy. The web
 *  afterActionVocabulary module maps them to Ontario-grounded labels. */
export const AFTER_ACTION_ENTRY_KINDS = ['finding', 'recommendation', 'debrief_note'] as const;
export const AfterActionEntryKindSchema = z.enum(AFTER_ACTION_ENTRY_KINDS);
export type AfterActionEntryKind = z.infer<typeof AfterActionEntryKindSchema>;

export const AfterActionEntrySchema = z.object({
  id: z.string(),
  kind: AfterActionEntryKindSchema,
  section: z.string().nullable(),
  body: z.string(),
  /** The human author of record — every entry carries one (App A #1). */
  authored_by: z.string(),
  created_at: z.string(),
  /** When set, this entry was superseded by the referenced entry (append-only correction;
   *  the original is NEVER overwritten). A live view shows only entries with this null. */
  superseded_by: z.string().nullable(),
});
export type AfterActionEntry = z.infer<typeof AfterActionEntrySchema>;

export const AfterActionReportStatusSchema = z.enum(['draft', 'final']);
export type AfterActionReportStatus = z.infer<typeof AfterActionReportStatusSchema>;

export const AfterActionReportSchema = z.object({
  id: z.string(),
  status: AfterActionReportStatusSchema,
  opened_by: z.string(),
  opened_at: z.string(),
  finalized_by: z.string().nullable(),
  finalized_at: z.string().nullable(),
});
export type AfterActionReport = z.infer<typeof AfterActionReportSchema>;

/* ------------------------------------------------------------------ *
 * Factual spine — a read-model; rollups projected from the Final SITREP
 * ------------------------------------------------------------------ */

export const AfterActionPeriodSchema = z.object({
  id: z.string(),
  period_number: z.number().int(),
  start_at: z.string(),
  end_at: z.string(),
  status: z.string(),
});
export type AfterActionPeriod = z.infer<typeof AfterActionPeriodSchema>;

export const AfterActionSitrepRefSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** standalone | initial | update | final */
  report_version: z.string(),
  report_sequence: z.number().int().nullable(),
  response_level_max: z.number().int().nullable(),
  created_at: z.string(),
});
export type AfterActionSitrepRef = z.infer<typeof AfterActionSitrepRefSchema>;

export const AfterActionCommandRefSchema = z.object({
  agency_name: z.string(),
  is_lead: z.boolean(),
  unified: z.boolean(),
  functional_role: z.string().nullable(),
  command_officer_name: z.string().nullable(),
  joined_at: z.string(),
  /** null while still a live participant (released → left the command). */
  released_at: z.string().nullable(),
});
export type AfterActionCommandRef = z.infer<typeof AfterActionCommandRefSchema>;

export const AfterActionDeclarationRefSchema = z.object({
  declaration_number: z.string(),
  authority_kind: z.string(),
  declaring_authority_name: z.string(),
  status: z.string(),
  declared_at: z.string(),
  terminated_at: z.string().nullable(),
});
export type AfterActionDeclarationRef = z.infer<typeof AfterActionDeclarationRefSchema>;

/**
 * The reactivation history as a FIRST-CLASS, COUNTED beat — never smoothed. Each completed
 * deactivation→reactivation cycle (the false stand-down held honestly by lifecycle_reactivations
 * / deactivation_gaps, #535/#485) is rendered "Demob #1 → gap → Reactivated → Demob #2". A
 * timeline that smooths the gap into one continuous incident is a reassurance artifact about our
 * own performance.
 */
export const AfterActionReactivationSchema = z.object({
  /** Count of completed deactivation→reactivation cycles (0 = never reactivated). */
  cycles: z.number().int(),
  gaps: z.array(IncidentReactivationSchema),
});
export type AfterActionReactivation = z.infer<typeof AfterActionReactivationSchema>;

/**
 * Operational rollups — PROJECTED FROM THE FINAL SITREP's frozen `structured_data`. Identical
 * bytes to the Final SITREP (PIN #4). The unknown/floor flags (PIN #5) are DERIVED from the
 * same source so the report never prints "all accounted for" while any community is pending, and
 * totals render as FLOORS when anything is unknown.
 */
export const AfterActionRollupsSchema = z.object({
  /** The Final SITREP these rollups were read from (null = no Final SITREP on file). */
  final_sitrep_id: z.string().nullable(),
  final_sitrep_sequence: z.number().int().nullable(),
  community_impact: SitrepCommunityImpactSchema.nullable(),
  resource_requests: SitrepResourceRequestsSchema.nullable(),
  staging_areas: SitrepStagingAreasSchema.nullable(),
  sectors: SitrepSectorsSchema.nullable(),
  /** ≥1 linked community has no recorded headcount (accounting_state 'pending'). */
  communities_pending: z.number().int(),
  /** Any community reported unreachable (the unknown companion is non-zero). */
  any_unreachable: z.boolean(),
  /** communities_pending>0 OR any_unreachable ⇒ evac totals render as FLOORS ("≥ N"). */
  totals_are_floors: z.boolean(),
  /** TRUE ONLY when every community is counted, none remaining, none unreachable. Gates the
   *  literal phrase "all accounted for" — never printed while this is false. */
  all_accounted_for: z.boolean(),
});
export type AfterActionRollups = z.infer<typeof AfterActionRollupsSchema>;

export const AfterActionFactualSchema = z.object({
  operational_periods: z.array(AfterActionPeriodSchema),
  sitrep_chain: z.array(AfterActionSitrepRefSchema),
  command_history: z.array(AfterActionCommandRefSchema),
  declarations: z.array(AfterActionDeclarationRefSchema),
  reactivation: AfterActionReactivationSchema,
  rollups: AfterActionRollupsSchema,
  /** Count of incident-scoped recorded operator actions (the AAR Replay timeline, §4.43). */
  recorded_action_count: z.number().int(),
});
export type AfterActionFactual = z.infer<typeof AfterActionFactualSchema>;

/* ------------------------------------------------------------------ *
 * The assembled response — GET /api/incidents/:id/after-action
 * ------------------------------------------------------------------ */

export const AfterActionIncidentSchema = z.object({
  id: z.string(),
  incident_number: z.string(),
  name: z.string(),
  hazard_type: z.string(),
  lifecycle_state: z.string(),
  status: z.string(),
  opened_at: z.string(),
  closed_at: z.string().nullable(),
});
export type AfterActionIncident = z.infer<typeof AfterActionIncidentSchema>;

export const AfterActionResponseSchema = z.object({
  incident: AfterActionIncidentSchema,
  /** The incident has deactivated (§8) — Planning may open the report / an export may emit. */
  incident_deactivated: z.boolean(),
  /** The report may be opened now (deactivated AND none exists yet). */
  can_open: z.boolean(),
  /** The PDF may be exported now (deactivated AND a report exists). */
  can_export: z.boolean(),
  report: AfterActionReportSchema.nullable(),
  /** Live entries (superseded_by IS NULL), newest-authored first within kind grouping. */
  entries: z.array(AfterActionEntrySchema),
  factual: AfterActionFactualSchema,
  /** The person-evaluation ban notice rendered on the surface (App A #1). */
  person_evaluation_notice: z.string(),
});
export type AfterActionResponse = z.infer<typeof AfterActionResponseSchema>;

/* ------------------------------------------------------------------ *
 * Request bodies
 * ------------------------------------------------------------------ */

export const AddAfterActionEntrySchema = z.object({
  kind: AfterActionEntryKindSchema,
  section: z.string().trim().max(120).nullish(),
  body: z.string().trim().min(1).max(4000),
  /** The human author of record — REQUIRED (the COP never authors). */
  authored_by: z.string().trim().min(1).max(200),
  /** When correcting an existing entry: the entry this one supersedes (append-only edit). */
  supersedes: z.string().uuid().nullish(),
});
export type AddAfterActionEntryRequest = z.infer<typeof AddAfterActionEntrySchema>;

export const FinalizeAfterActionSchema = z.object({
  /** The human who finalizes the report (author of record for the finalize act). */
  finalized_by: z.string().trim().min(1).max(200),
});
export type FinalizeAfterActionRequest = z.infer<typeof FinalizeAfterActionSchema>;

/** The static notice the surface renders to make the person-evaluation ban legible (App A #1). */
export const AFTER_ACTION_PERSON_EVAL_NOTICE =
  'Findings name actions, functions, and gaps — never individuals. An after-action report ' +
  'evaluates the actions of the incident responders (Ontario IMS 2.0, Appendix A #1); it is a ' +
  'disclosable record and must not name or evaluate a person.';
